/**
 * Bundle Builder Alt (V1.7 Design)
 * A/B test variant with continuous progress bar, category pills, rectangular swatch chips
 */

(function(window, document) {
  'use strict';

  // ===========================================================================
  // PRICE CALCULATOR (Ported from bundle-builder-360.js)
  // ===========================================================================

  class PriceCalculator {
    constructor(discountTiers, freeShippingThreshold) {
      this.discountTiers = discountTiers || [
        { minCount: 4, percent: 30 },
        { minCount: 3, percent: 20 },
        { minCount: 2, percent: 15 },
        { minCount: 1, percent: 0 }
      ];
      // null when the shipping profile has no rule for this market. Do NOT
      // substitute a literal: a hardcoded $150 fallback here is the same class of
      // bug that had the cart badge advertising A$65 to US shoppers. Null means
      // "we don't know", and callers must stay quiet rather than guess.
      this.freeShippingThreshold =
        (typeof freeShippingThreshold === 'number' && freeShippingThreshold >= 0)
          ? freeShippingThreshold
          : null;
      // Multi-select: Map<categoryKey, Map<variantId, selection>>
      this.selections = new Map();
    }

    toggleSelection(categoryKey, data) {
      if (!data || !data.variantId) return false;

      // Get or create category map
      if (!this.selections.has(categoryKey)) {
        this.selections.set(categoryKey, new Map());
      }
      const categorySelections = this.selections.get(categoryKey);

      // Toggle: if exists, remove; otherwise add
      if (categorySelections.has(data.variantId)) {
        categorySelections.delete(data.variantId);
        // Clean up empty categories
        if (categorySelections.size === 0) {
          this.selections.delete(categoryKey);
        }
        return false; // Deselected
      } else {
        categorySelections.set(data.variantId, {
          variantId: data.variantId,
          price: parseFloat(data.price) || 0,
          name: data.name || '',
          image: data.productImage || null
        });
        return true; // Selected
      }
    }

    // Legacy method for compatibility
    setSelection(categoryKey, data) {
      if (!data || data.variantId === '' || data.variantId === null) {
        this.selections.delete(categoryKey);
      } else {
        // For single selection, clear category first then add
        this.selections.set(categoryKey, new Map());
        this.selections.get(categoryKey).set(data.variantId, {
          variantId: data.variantId,
          price: parseFloat(data.price) || 0,
          name: data.name || '',
          image: data.productImage || null
        });
      }
    }

    getSubtotal() {
      let total = 0;
      this.selections.forEach(categoryMap => {
        categoryMap.forEach(selection => {
          total += selection.price;
        });
      });
      return total;
    }

    getItemCount() {
      let count = 0;
      this.selections.forEach(categoryMap => {
        count += categoryMap.size;
      });
      return count;
    }

    // null (not 0) when the threshold is unknown, so callers can tell
    // "nothing more to pay" apart from "we have no idea".
    getAmountToFreeShipping() {
      if (this.freeShippingThreshold === null) return null;
      const subtotal = this.getSubtotal() * 100; // Convert to cents
      const remaining = this.freeShippingThreshold - subtotal;
      return remaining > 0 ? remaining : 0;
    }

    hasFreeShipping() {
      const remaining = this.getAmountToFreeShipping();
      if (remaining === null) return false;
      return remaining === 0 && this.getItemCount() > 0;
    }

    getCurrentTier() {
      const count = this.getItemCount();
      for (const tier of this.discountTiers) {
        if (count >= tier.minCount) {
          return tier;
        }
      }
      return { minCount: 0, percent: 0 };
    }

    getNextTier() {
      const count = this.getItemCount();
      const sortedTiers = [...this.discountTiers].sort((a, b) => a.minCount - b.minCount);
      return sortedTiers.find(tier => tier.minCount > count) || null;
    }

    getDiscountAmount() {
      const tier = this.getCurrentTier();
      const subtotal = this.getSubtotal();
      return subtotal * (tier.percent / 100);
    }

    getTotal() {
      return this.getSubtotal() - this.getDiscountAmount();
    }

    getAllSelections() {
      const result = [];
      this.selections.forEach((categoryMap, category) => {
        categoryMap.forEach((data, variantId) => {
          result.push({ category, ...data });
        });
      });
      return result;
    }

    hasSelectionInCategory(categoryKey) {
      return this.selections.has(categoryKey) && this.selections.get(categoryKey).size > 0;
    }

    getCategorySelections(categoryKey) {
      if (!this.selections.has(categoryKey)) return [];
      return Array.from(this.selections.get(categoryKey).values());
    }
  }

  // ===========================================================================
  // CART INTEGRATION (Ported from bundle-builder-360.js)
  // ===========================================================================

  class CartIntegration {
    constructor() {
      this.isProcessing = false;
    }

    async addItems(items) {
      if (this.isProcessing) return { success: false, error: 'Already processing' };
      if (items.length === 0) return { success: false, error: 'No items to add' };

      this.isProcessing = true;

      try {
        const validItems = items.filter(item => item.variantId && item.variantId.length > 0);

        if (validItems.length === 0) {
          this.isProcessing = false;
          return { success: false, error: 'No valid items to add' };
        }

        const payload = {
          items: validItems.map(item => ({
            id: item.variantId,
            quantity: 1
          }))
        };

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Dispatch cart updated event (cart directly as detail, not wrapped)
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

        // Update cart drawer if available
        if (window.wetheme && typeof window.wetheme.updateCartDrawer === 'function') {
          window.wetheme.updateCartDrawer(cart);
        }

        this.isProcessing = false;
        return { success: true, cart };

      } catch (error) {
        console.error('[BundleBuilderAlt] Cart error:', error);
        this.isProcessing = false;
        return { success: false, error: error.message };
      }
    }
  }

  // ===========================================================================
  // BUNDLE BUILDER ALT CONTROLLER
  // ===========================================================================

  class BundleBuilderAlt {
    constructor(container) {
      this.container = container;
      this.config = this.parseConfig();
      this.priceCalculator = new PriceCalculator(
        this.config.discountTiers,
        // Pass through as-is — null propagates, and PriceCalculator treats null
        // as unknown. `|| 15000` here defeated the whole point.
        this.config.freeShippingThreshold
      );
      this.cartIntegration = new CartIntegration();
      this.activeCategories = new Set();

      this.cacheElements();
      this.bindEvents();
      this.initialize();
    }

    parseConfig() {
      const configEl = this.container.querySelector('[data-alt-config]');
      if (!configEl) {
        console.error('[BundleBuilderAlt] No config found');
        return {};
      }
      try {
        return JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[BundleBuilderAlt] Config parse error:', e);
        return {};
      }
    }

    cacheElements() {
      // Hero/Gallery
      this.heroWrapper = this.container.querySelector('.builder-alt-hero-wrapper');
      this.heroImage = this.container.querySelector('[data-hero-image] img');
      this.defaultHeroSrc = this.heroImage?.dataset.defaultSrc;
      this.expandHint = this.container.querySelector('[data-expand-hint]');
      this.builderContainer = this.container.querySelector('.builder-alt-container');

      // Progress
      this.progressFill = this.container.querySelector('[data-progress-fill]');
      this.progressMessage = this.container.querySelector('[data-progress-message]');

      // Pills
      this.pills = this.container.querySelectorAll('.builder-alt-pill');

      // Swatches panels
      this.swatchPanels = this.container.querySelectorAll('[data-category-panel]');

      // Chips
      this.chips = this.container.querySelectorAll('.builder-alt-chip');

      // Footer
      this.totalPrice = this.container.querySelector('[data-total-price]');
      this.originalPrice = this.container.querySelector('[data-original-price]');
      this.savingsPill = this.container.querySelector('[data-savings-pill]');
      this.discountPercent = this.container.querySelector('[data-discount-percent]');
      this.itemCount = this.container.querySelector('[data-item-count]');
      this.trustBadge = this.container.querySelector('[data-trust-badge]');
      this.ctaBtn = this.container.querySelector('[data-add-to-cart]');

      // Accordions
      this.accordionHeaders = this.container.querySelectorAll('.builder-alt-accordion-header');
    }

    bindEvents() {
      // Category pills - toggle selection
      this.pills.forEach(pill => {
        pill.addEventListener('click', () => this.toggleCategory(pill));
      });

      // Swatch chips - select variant
      this.chips.forEach(chip => {
        chip.addEventListener('click', () => this.selectChip(chip));
      });

      // Accordion toggle
      this.accordionHeaders.forEach(header => {
        header.addEventListener('click', () => this.toggleAccordion(header));
      });

      // Add to cart
      if (this.ctaBtn) {
        this.ctaBtn.addEventListener('click', () => this.addToCart());
      }

      // Hero image expand toggle (mobile only)
      if (this.heroImage) {
        this.heroImage.addEventListener('click', () => this.toggleExpand());
      }
      if (this.expandHint) {
        this.expandHint.addEventListener('click', () => this.toggleExpand());
      }
    }

    initialize() {
      // Flag to prevent hero image change on init
      this.isInitializing = true;

      // Select first category by default
      if (this.pills.length > 0) {
        this.toggleCategory(this.pills[0]);
      }
      this.updateAllUI();

      // Allow image changes after init
      this.isInitializing = false;
    }

    // ===========================================================================
    // CATEGORY PILLS
    // ===========================================================================

    toggleCategory(pill) {
      const categoryKey = pill.dataset.category;

      // Clear selection from all pills first (single-select behavior)
      this.pills.forEach(p => p.classList.remove('selected'));

      // Select this pill and show its panel
      pill.classList.add('selected');
      this.showSwatchPanel(categoryKey);

      this.updateAllUI();
    }

    showSwatchPanel(categoryKey) {
      // Hide all swatch panels first
      this.swatchPanels.forEach(panel => {
        panel.style.display = 'none';
      });

      // Hide all details panels
      const detailsPanels = this.container.querySelectorAll('[data-category-details]');
      detailsPanels.forEach(panel => {
        panel.style.display = 'none';
      });

      // Show the active category's swatch panel
      const panel = this.container.querySelector(`[data-category-panel="${categoryKey}"]`);
      if (panel) {
        panel.style.display = 'block';
      }

      // Show the active category's details panel
      const detailsPanel = this.container.querySelector(`[data-category-details="${categoryKey}"]`);
      if (detailsPanel) {
        detailsPanel.style.display = 'block';
      }

      // Update hero image to show this category's product
      this.updateImageForCategory(categoryKey);
    }

    hideSwatchPanel(categoryKey) {
      const panel = this.container.querySelector(`[data-category-panel="${categoryKey}"]`);
      if (panel) {
        panel.style.display = 'none';
      }

      // Show another active category's panel if any
      if (this.activeCategories.size > 0) {
        const nextCategory = Array.from(this.activeCategories)[this.activeCategories.size - 1];
        this.showSwatchPanel(nextCategory);
      }
    }

    // ===========================================================================
    // SWATCH CHIPS
    // ===========================================================================

    selectChip(chip) {
      const categoryKey = chip.dataset.category;
      const variantId = chip.dataset.variantId;
      const price = chip.dataset.price;
      const name = chip.dataset.name;
      const productImage = chip.dataset.productImage;
      const isAvailable = chip.dataset.available !== 'false';

      // Sold out items: show preview only, don't add to bundle
      if (!isAvailable) {
        this.updatePreviewImage(productImage, name);
        // Could add "Notify me" modal here in future
        return;
      }

      // Multi-select toggle: each chip toggles independently
      const isNowSelected = this.priceCalculator.toggleSelection(categoryKey, {
        variantId,
        price: price / 100, // Convert from cents
        name,
        productImage
      });

      // Update chip visual state
      if (isNowSelected) {
        chip.classList.add('selected');
        // Update hero image with the newly selected item
        this.updatePreviewImage(productImage, name);
      } else {
        chip.classList.remove('selected');
        // Show another selected item's image, or default
        const categorySelections = this.priceCalculator.getCategorySelections(categoryKey);
        if (categorySelections.length > 0) {
          const lastSelection = categorySelections[categorySelections.length - 1];
          this.updatePreviewImage(lastSelection.image, lastSelection.name);
        } else {
          this.updatePreviewImage(null, null);
        }
      }

      this.updateAllUI();
    }

    // ===========================================================================
    // IMAGE/GALLERY
    // ===========================================================================

    updatePreviewImage(imageUrl, variantName) {
      if (!this.heroImage) return;

      // Localized fallback alt; data attribute is set in the section liquid.
      const fallbackAlt = this.container.dataset.i18nBundlePreview || 'Bundle preview';

      // If no image URL, revert to default
      if (!imageUrl) {
        if (this.defaultHeroSrc && this.heroImage.src !== this.defaultHeroSrc) {
          this.animateImageSwap(this.defaultHeroSrc, fallbackAlt);
        }
        return;
      }

      this.animateImageSwap(imageUrl, variantName || fallbackAlt);
    }

    animateImageSwap(newSrc, altText) {
      if (!this.heroImage) return;

      // Fade out and scale down (200ms)
      this.heroImage.style.opacity = '0.7';
      this.heroImage.style.transform = 'scale(0.98)';

      // Swap image mid-transition
      setTimeout(() => {
        this.heroImage.src = newSrc;
        this.heroImage.alt = altText;
        // Fade in and scale back to normal
        this.heroImage.style.opacity = '1';
        this.heroImage.style.transform = 'scale(1)';
      }, 200);
    }

    updateImageForCategory(categoryKey) {
      // Don't change hero image during initialization - keep the default
      if (this.isInitializing) return;

      // Get selection for this category
      const selections = this.priceCalculator.getAllSelections();
      const selection = selections.find(s => s.category === categoryKey);

      if (selection && selection.image) {
        this.updatePreviewImage(selection.image, selection.name);
      } else {
        // Try to get the first variant's product image from the panel
        const panel = this.container.querySelector(`[data-category-panel="${categoryKey}"]`);
        if (panel) {
          const firstChip = panel.querySelector('.builder-alt-chip[data-product-image]');
          if (firstChip) {
            const productImage = firstChip.dataset.productImage;
            const name = firstChip.dataset.name || categoryKey;
            if (productImage) {
              this.updatePreviewImage(productImage, name);
              return;
            }
          }
        }
        // Fallback to default
        this.updatePreviewImage(null, null);
      }
    }

    toggleExpand() {
      // Only works on mobile (< 1024px)
      if (window.innerWidth >= 1024) return;

      const isExpanded = this.builderContainer?.classList.contains('stage-expanded');

      if (isExpanded) {
        // Collapse
        this.builderContainer.classList.remove('stage-expanded');
        if (this.expandHint) {
          this.expandHint.innerHTML = `
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 5V1h4M13 5V1H9M1 9v4h4M13 9v4H9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Tap to View</span>
          `;
        }
      } else {
        // Expand
        this.builderContainer.classList.add('stage-expanded');
        if (this.expandHint) {
          this.expandHint.innerHTML = `
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 1H1v4M9 1h4v4M5 13H1V9M9 13h4V9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Show Options</span>
          `;
        }
      }
    }

    // ===========================================================================
    // UI UPDATES
    // ===========================================================================

    updateAllUI() {
      this.updateProgressBar();
      this.updateFooter();
      this.updatePillBadges();
    }

    updateProgressBar() {
      const itemCount = this.priceCalculator.getItemCount();
      const totalCategories = this.config.categories?.length || 4;
      const percentage = Math.round((itemCount / totalCategories) * 100);

      // Update fill width
      if (this.progressFill) {
        this.progressFill.style.width = `${percentage}%`;
      }

      // Update message with exact user-specified wording
      if (this.progressMessage) {
        let message = '';
        switch (itemCount) {
          case 0:
            message = 'Add 2 items to unlock <span class="highlight">15% off</span> your cart';
            break;
          case 1:
            message = 'Add 1 more to unlock <span class="highlight">15% off</span> your cart';
            break;
          case 2:
            message = 'Add 1 more to unlock <span class="highlight">20% off</span> your cart';
            break;
          case 3:
            message = 'Add 1 more to unlock <span class="highlight">30% off</span> your cart';
            break;
          default: // 4+
            message = '🎉 Max Discount Unlocked: <span class="highlight">30% off</span> your cart';
            break;
        }
        this.progressMessage.innerHTML = message;
      }
    }

    updateFooter() {
      const itemCount = this.priceCalculator.getItemCount();
      const subtotal = this.priceCalculator.getSubtotal();
      const total = this.priceCalculator.getTotal();
      const discount = this.priceCalculator.getDiscountAmount();
      const tier = this.priceCalculator.getCurrentTier();

      // Update item count
      if (this.itemCount) {
        this.itemCount.textContent = itemCount;
      }

      // Update prices
      if (this.totalPrice) {
        this.totalPrice.textContent = this.formatMoney(total * 100);
      }

      if (this.originalPrice) {
        if (discount > 0) {
          this.originalPrice.textContent = this.formatMoney(subtotal * 100);
          this.originalPrice.style.display = 'inline';
        } else {
          this.originalPrice.style.display = 'none';
        }
      }

      // Update savings pill
      if (this.savingsPill && this.discountPercent) {
        if (tier.percent > 0) {
          this.discountPercent.textContent = tier.percent;
          this.savingsPill.style.display = 'inline-flex';
        } else {
          this.savingsPill.style.display = 'none';
        }
      }

      // Update CTA button state
      if (this.ctaBtn) {
        this.ctaBtn.disabled = itemCount === 0;
      }
    }

    updatePillBadges() {
      // Add visual indicator to pills that have selections
      this.pills.forEach(pill => {
        const categoryKey = pill.dataset.category;
        const selections = this.priceCalculator.getAllSelections();
        const hasSelection = selections.some(s => s.category === categoryKey);

        if (hasSelection) {
          pill.classList.add('has-selection');
        } else {
          pill.classList.remove('has-selection');
        }
      });
    }

    // ===========================================================================
    // ACCORDIONS
    // ===========================================================================

    toggleAccordion(header) {
      const accordion = header.parentElement;
      const isOpen = accordion.classList.contains('open');
      const expanded = header.getAttribute('aria-expanded') === 'true';

      // Close all accordions
      this.container.querySelectorAll('.builder-alt-accordion').forEach(acc => {
        acc.classList.remove('open');
        acc.querySelector('.builder-alt-accordion-header').setAttribute('aria-expanded', 'false');
      });

      // Open this one if it wasn't already open
      if (!isOpen) {
        accordion.classList.add('open');
        header.setAttribute('aria-expanded', 'true');
      }
    }

    // ===========================================================================
    // ADD TO CART
    // ===========================================================================

    async addToCart() {
      const selections = this.priceCalculator.getAllSelections();

      if (selections.length === 0) {
        console.warn('[BundleBuilderAlt] No items selected');
        return;
      }

      // Show loading state
      this.setButtonState('loading');

      const result = await this.cartIntegration.addItems(selections);

      if (result.success) {
        this.setButtonState('success');

        // Open cart drawer
        const cartSlideoutWrapper = document.querySelector('#cartSlideoutWrapper');
        if (cartSlideoutWrapper) {
          setTimeout(() => {
            const event = new CustomEvent('cart:open');
            cartSlideoutWrapper.dispatchEvent(event);
          }, 300); // Slight delay for smooth transition
        }

        // Reset after delay
        setTimeout(() => {
          this.setButtonState('default');
        }, 2500);
      } else {
        console.error('[BundleBuilderAlt] Add to cart failed:', result.error);
        this.setButtonState('default');

        // Show error to user
        if (window.wetheme && window.wetheme.showNotification) {
          window.wetheme.showNotification('Unable to add to cart. Please try again.', 'error');
        } else {
          alert('Unable to add items to cart. Please try again.');
        }
      }
    }

    setButtonState(state) {
      if (!this.ctaBtn) return;

      this.ctaBtn.dataset.state = state;

      switch (state) {
        case 'loading':
          this.ctaBtn.disabled = true;
          break;
        case 'success':
          this.ctaBtn.disabled = true;
          break;
        default:
          this.ctaBtn.disabled = this.priceCalculator.getItemCount() === 0;
          this.ctaBtn.removeAttribute('data-state');
      }
    }

    // ===========================================================================
    // UTILITIES
    // ===========================================================================

    formatMoney(cents) {
      // config.moneyFormat is the SHOP's format string and is Australia's on every
      // market, so a EUR cart's builder footer rendered "$45.00". When the shopper
      // is charged in another currency, format from that instead. Plain text, not
      // money HTML: callers assign this to textContent, so a <span> would be shown
      // literally — which is why this does not delegate to theme.formatMoney.
      // Locale pinned to 'en' to match the theme's leading-symbol convention.
      const pc = this.config.presentmentCurrency;
      const sc = this.config.shopCurrency;
      if (pc && sc && pc !== sc && typeof Intl !== 'undefined') {
        try {
          return new Intl.NumberFormat('en', {
            style: 'currency', currency: pc, currencyDisplay: 'narrowSymbol',
          }).format(cents / 100);
        } catch (e) {
          try {
            return new Intl.NumberFormat('en', { style: 'currency', currency: pc })
              .format(cents / 100);
          } catch (e2) { /* unknown code — fall through */ }
        }
      }
      const format = this.config.moneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2);

      return format
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', Math.round(cents / 100))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    }
  }

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  function initBundleBuilderAlt() {
    const containers = document.querySelectorAll('[data-bundle-builder-alt]');

    containers.forEach(container => {
      if (container.dataset.altInitialized) return;

      try {
        container._bundleBuilderAlt = new BundleBuilderAlt(container);
        container.dataset.altInitialized = 'true';
        console.log('[BundleBuilderAlt] Initialized');
      } catch (error) {
        console.error('[BundleBuilderAlt] Init error:', error);
      }
    });
  }

  // DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleBuilderAlt);
  } else {
    initBundleBuilderAlt();
  }

  // Expose for debugging
  window.BundleBuilderAlt = BundleBuilderAlt;

})(window, document);
